import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Form, InputGroup, Alert } from 'react-bootstrap';
import axiosInstance from '../../api/axiosInstance';
import colors from '../../constants/colors';
import { formatPrice } from '../../utils/formatPrice';
// Import React Icons
import { FaSearch, FaFileAlt, FaExternalLinkAlt } from 'react-icons/fa';

const DigitalProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('price_asc');
  
  // Define fetchProducts with useCallback - specifically fetch digital products
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Prepare query params based on filters and sorting
      const sortParams = sortOption.split('_');
      const sortBy = sortParams[0];
      const sortOrder = sortParams[1];
      
      // Build query string - specifically for digital products
      let queryString = `?sortBy=${sortBy}&order=${sortOrder}&isDigital=true`;
      
      const response = await axiosInstance.get(`/product${queryString}`);
      
      if(searchTerm.length > 0){
        setProducts(response.rows.filter((product) => product.name.toLowerCase().includes(searchTerm.toLowerCase())));
      } else {
        setProducts(response.rows);
      }
      
      setError('');
    } catch (err) {
      console.error("Error fetching digital products:", err);
      setError('Erro ao carregar produtos digitais. Por favor tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  }, [sortOption, searchTerm]);
  
  // Fetch products on component mount and when dependencies change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchProducts();
  };
  

  return (
    <Container className="py-5">
      <h1 className="mb-4" style={{ color: colors.dark }}>Produtos Digitais</h1>
      
      {/* Filters and Search */}
      <Row className="mb-4">
        <Col md={6}>
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Pesquisar produtos digitais..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button 
                type="submit" 
                style={{ backgroundColor: colors.secondary, borderColor: colors.secondary, color: colors.light }}
              >
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col md={6}>
          <Form.Select 
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="price_asc">Preço: Menor para Maior</option>
            <option value="price_desc">Preço: Maior para Menor</option>
          </Form.Select>
        </Col>
      </Row>
      
      {/* Error Message */}
      {error && <Alert style={{ backgroundColor: colors.danger, color: colors.light, borderColor: colors.danger }}>{error}</Alert>}
      
      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" style={{ color: colors.secondary }}/>
        </div>
      ) : (
        <>
          {/* Product Grid */}
          {products.length === 0 ? (
            <Alert style={{ backgroundColor: colors.secondary, color: colors.light, borderColor: colors.secondary }}>
              Nenhum produto digital encontrado. Tente ajustar os filtros de pesquisa.
            </Alert>
          ) : (
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {products.map((product) => (
                <Col key={product.id}>
                  <Card className="h-100 product-card">
                    {product.images && product.images[0] ? (
                      <Card.Img 
                        variant="top" 
                        src={product.images[0].src} 
                        alt={product.name}
                        loading="lazy"
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div 
                        className="d-flex align-items-center justify-content-center" 
                        style={{ height: '200px', backgroundColor: colors.background }}
                      >
                        <FaFileAlt style={{ fontSize: '3rem', color: colors.secondary }} />
                      </div>
                    )}
                    <Card.Body className="d-flex flex-column">
                      <Card.Title style={{ color: colors.dark }}>{product.name}</Card.Title>
                      <Card.Text className="small mb-2" style={{ color: colors.textGray }}>
                        {product.description?.length > 80 
                          ? product.description.substring(0, 80) + '...'
                          : product.description}
                      </Card.Text>
                      <div className="mt-auto">
                        <Card.Text className="fw-bold fs-5 mb-2" style={{ color: colors.dark }}>
                          {formatPrice(product.price)}€
                        </Card.Text>
                        <div className="d-flex gap-2">
                          {product.link && (
                            <Button 
                              href={product.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-grow-1"
                              style={{ borderColor: colors.secondary, color: colors.light, backgroundColor: colors.secondary }}
                            >
                              Aceder ao Conteúdo <FaExternalLinkAlt className="ms-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

export default DigitalProducts;
